import React, { useEffect, useRef, useState } from "react";
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import {
  MoreVert, Edit, ArrowUpward, ArrowDownward, KeyboardArrowUp, KeyboardArrowDown,
  Visibility, VisibilityOff, ContentCopy, Delete,
} from "@mui/icons-material";
import { useScrollytellTranslation } from "./scrollytellContext";

export const MiniSectionMenu = ({
  index,
  totalSections,
  excludeFromNav = false,
  onEditTitle,
  onInsertAbove,
  onInsertBelow,
  onMoveUp,
  onMoveDown,
  onToggleNav,
  onCopy,
  onDelete,
}) => {
  const t = useScrollytellTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const buttonRef = useRef(null);
  const menuPaperRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      const target = e.target;
      if (
        buttonRef.current?.contains(target) ||
        menuPaperRef.current?.contains(target) ||
        target?.closest?.(".MuiMenu-paper")
      ) {
        return;
      }
      setAnchorEl(null);
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setAnchorEl(null);
    };

    document.addEventListener("mousedown", handleOutside, true);
    document.addEventListener("touchstart", handleOutside, true);
    document.addEventListener("keydown", handleKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", handleOutside, true);
      document.removeEventListener("touchstart", handleOutside, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [open]);

  const handleClick = (e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); };
  const handleClose = (e) => { e?.stopPropagation?.(); setAnchorEl(null); };
  const wrapAction = (fn) => (e) => { e.stopPropagation(); handleClose(); fn?.(); };

  const menuItems = [
    { label: t("editTitle", "Rediger tittel..."), icon: <Edit fontSize="small" />, color: "#93C5FD", action: onEditTitle },
    { label: t("insertAbove", "Sett inn ny seksjon over"), icon: <ArrowUpward fontSize="small" />, color: "#34D399", action: onInsertAbove },
    { label: t("insertBelow", "Sett inn ny seksjon under"), icon: <ArrowDownward fontSize="small" />, color: "#34D399", action: onInsertBelow },
    { label: t("moveUp", "Flytt opp"), icon: <KeyboardArrowUp fontSize="small" />, color: "#CBD5E1", action: onMoveUp, disabled: index === 0 },
    { label: t("moveDown", "Flytt ned"), icon: <KeyboardArrowDown fontSize="small" />, color: "#CBD5E1", action: onMoveDown, disabled: index === totalSections - 1 },
    {
      label: excludeFromNav ? t("showInNav", "Vis i meny") : t("hideFromNav", "Skjul fra meny"),
      icon: excludeFromNav ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />,
      color: "#FCD34D", action: onToggleNav,
    },
    { label: t("copySectionTooltip", "Kopier seksjon"), icon: <ContentCopy fontSize="small" />, color: "#60A5FA", action: onCopy },
    {
      label: t("deleteSectionTooltip", "Slett seksjon..."), icon: <Delete fontSize="small" />, color: "#F87171",
      action: onDelete, isDelete: true,
    },
  ];

  return (
    <>
      <IconButton
        ref={buttonRef}
        size="small"
        onClick={handleClick}
        data-testid={`mini-section-menu-btn-${index}`}
        aria-label={t("sectionMenuLabel", "Handlinger for seksjon")}
        sx={{
          color: "rgba(255, 255, 255, 0.75)",
          padding: "4px",
          "&:hover": { color: "#FFFFFF", backgroundColor: "rgba(255, 255, 255, 0.12)" },
        }}
      >
        <MoreVert fontSize="small" />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transitionDuration={0}
        onClick={(e) => e.stopPropagation()}
        slotProps={{
          paper: {
            ref: menuPaperRef,
            sx: {
              backgroundColor: "#1E293B",
              color: "#F8FAFC",
              borderRadius: "8px",
              border: "1px solid rgba(255, 255, 255, 0.12)",
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.45)",
              minWidth: 190,
              py: 0.5,
              "& .MuiMenuItem-root": {
                fontSize: "12.5px", py: "6px", px: "12px", gap: "8px",
                "&:hover": { backgroundColor: "#334155" },
                "&.Mui-disabled": { opacity: 0.4, color: "rgba(255, 255, 255, 0.4)" },
              },
            },
          },
        }}
      >
        {menuItems.map((item, idx) => (
          <MenuItem
            key={idx}
            disabled={item.disabled}
            onClick={wrapAction(item.action)}
            sx={item.isDelete ? { color: "#F87171 !important", borderTop: "1px solid rgba(255, 255, 255, 0.08)", mt: 0.5 } : undefined}
          >
            <ListItemIcon sx={{ color: item.color, minWidth: 24 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

